import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Loader2, Plus, Copy, Trash2, Eye, Download, FileText, Sparkles, Link as LinkIcon, ArrowLeft, CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AssessmentsAdmin({ token }) {
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [view, setView] = useState('list'); // list | create | submissions
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);

  // create state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsedQuestions, setParsedQuestions] = useState([]);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [durationMinutes, setDurationMinutes] = useState(20);
  const [creating, setCreating] = useState(false);

  // submissions state
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [reportDialog, setReportDialog] = useState(null); // submission with report

  const loadAssessments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assessment/list`, authHeaders);
      setAssessments(res.data.assessments || []);
    } catch (e) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAssessments(); /* eslint-disable-next-line */ }, []);

  const handleParse = async () => {
    if (!rawText.trim()) { toast.error('Paste your MCQ text first'); return; }
    setParsing(true);
    try {
      const res = await axios.post(`${API}/assessment/parse-questions`, { raw_text: rawText }, authHeaders);
      if (!res.data.questions.length) {
        toast.error('No questions could be parsed. Check the format.');
      } else {
        setParsedQuestions(res.data.questions);
        toast.success(`Parsed ${res.data.count} question(s). Review correct answers below.`);
      }
    } catch (e) {
      toast.error('Parsing failed');
    } finally {
      setParsing(false);
    }
  };

  const setCorrect = (qi, oi) => {
    setParsedQuestions((prev) => prev.map((q, i) => (i === qi ? { ...q, correct_answer: oi } : q)));
  };

  const removeQuestion = (qi) => {
    setParsedQuestions((prev) => prev.filter((_, i) => i !== qi));
  };

  const handleCreate = async () => {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    if (!parsedQuestions.length) { toast.error('Parse questions first'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/assessment/create`, {
        title,
        description,
        duration_minutes: timeLimitEnabled ? Number(durationMinutes) : null,
        questions: parsedQuestions,
      }, authHeaders);
      toast.success('Assessment created');
      setTitle(''); setDescription(''); setRawText(''); setParsedQuestions([]);
      setTimeLimitEnabled(false); setDurationMinutes(20);
      setView('list');
      loadAssessments();
    } catch (e) {
      toast.error('Failed to create assessment');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/assessment/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied!');
  };

  const toggleActive = async (a) => {
    try {
      await axios.put(`${API}/assessment/update/${a.id}`, { is_active: !a.is_active }, authHeaders);
      loadAssessments();
    } catch (e) { toast.error('Update failed'); }
  };

  const deleteAssessment = async (a) => {
    if (!window.confirm(`Delete "${a.title}" and all its submissions?`)) return;
    try {
      await axios.delete(`${API}/assessment/delete/${a.id}`, authHeaders);
      toast.success('Deleted');
      loadAssessments();
    } catch (e) { toast.error('Delete failed'); }
  };

  const openSubmissions = async (a) => {
    setActiveAssessment(a);
    setView('submissions');
    setSubsLoading(true);
    try {
      const res = await axios.get(`${API}/assessment/submissions/${a.id}`, authHeaders);
      setSubmissions(res.data.submissions || []);
    } catch (e) {
      toast.error('Failed to load submissions');
    } finally {
      setSubsLoading(false);
    }
  };

  const generateReport = async (sub) => {
    setGeneratingId(sub.id);
    try {
      const res = await axios.post(`${API}/assessment/generate-report/${sub.id}`, {}, authHeaders);
      toast.success('AI report generated');
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, report_generated: true } : s)));
      setReportDialog({ ...sub, report: res.data.report });
    } catch (e) {
      toast.error('Report generation failed');
    } finally {
      setGeneratingId(null);
    }
  };

  const toggleConnected = async (sub) => {
    const next = !sub.connected;
    try {
      await axios.put(`${API}/assessment/submission/${sub.id}/connect`, { connected: next }, authHeaders);
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, connected: next } : s)));
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const assignTo = async (sub, value) => {
    try {
      await axios.put(`${API}/assessment/submission/${sub.id}/assign`, { assigned_to: value }, authHeaders);
      setSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, assigned_to: value } : s)));
    } catch (e) {
      toast.error('Failed to assign');
    }
  };

  const viewReport = async (sub) => {
    try {
      const res = await axios.get(`${API}/assessment/submission-detail/${sub.id}`, authHeaders);
      setReportDialog(res.data);
    } catch (e) { toast.error('Failed to load report'); }
  };

  const downloadPdf = async (sub) => {
    setDownloadingId(sub.id);
    try {
      const res = await axios.get(`${API}/assessment/download-report/${sub.id}`, {
        ...authHeaders, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${sub.candidate_name.replace(/ /g, '_')}_assessment_report.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download failed. Generate the report first.');
    } finally {
      setDownloadingId(null);
    }
  };

  // ============ LIST VIEW ============
  if (view === 'list') {
    return (
      <div data-testid="assessments-list-view">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Assessments</h1>
            <p className="text-slate-500">Create MCQ tests, share public links, and review candidate scores.</p>
          </div>
          <Button onClick={() => setView('create')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-assessment-btn">
            <Plus className="h-4 w-4 mr-2" /> New Assessment
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
        ) : assessments.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="no-assessments">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No assessments yet. Create your first one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Questions</TableHead>
                  <TableHead>Time Limit</TableHead>
                  <TableHead>Submissions</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {assessments.map((a) => (
                  <TableRow key={a.id} data-testid={`assessment-row-${a.id}`}>
                    <TableCell className="font-medium">{a.title}</TableCell>
                    <TableCell>{a.total_questions}</TableCell>
                    <TableCell>{a.duration_minutes ? `${a.duration_minutes} min` : '—'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{a.submission_count}</Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={a.is_active} onCheckedChange={() => toggleActive(a)} data-testid={`toggle-active-${a.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => copyLink(a.slug)} title="Copy public link" data-testid={`copy-link-${a.id}`}>
                          <LinkIcon className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openSubmissions(a)} title="View submissions" data-testid={`view-subs-${a.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteAssessment(a)} title="Delete" className="text-red-500 hover:text-red-600" data-testid={`delete-${a.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <ReportDialog reportDialog={reportDialog} setReportDialog={setReportDialog} />
      </div>
    );
  }

  // ============ CREATE VIEW ============
  if (view === 'create') {
    return (
      <div data-testid="assessment-create-view">
        <Button variant="ghost" onClick={() => setView('list')} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
        <h1 className="text-3xl font-bold text-slate-800 mb-6">Create Assessment</h1>

        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5 max-w-3xl">
          <div>
            <Label htmlFor="a-title">Title *</Label>
            <Input id="a-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Frontend Developer Screening" data-testid="assessment-title-input" />
          </div>
          <div>
            <Label htmlFor="a-desc">Description (optional)</Label>
            <Input id="a-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short instructions for candidates" data-testid="assessment-desc-input" />
          </div>

          <div className="flex items-center justify-between border border-slate-200 rounded-lg p-4">
            <div>
              <Label className="cursor-pointer">Enable time limit</Label>
              <p className="text-xs text-slate-400">Auto-submits when time runs out</p>
            </div>
            <div className="flex items-center gap-3">
              {timeLimitEnabled && (
                <Input type="number" min="1" value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)}
                  className="w-20" data-testid="duration-input" />
              )}
              <Switch checked={timeLimitEnabled} onCheckedChange={setTimeLimitEnabled} data-testid="time-limit-toggle" />
            </div>
          </div>

          <div>
            <Label htmlFor="a-raw">Paste MCQ text from ChatGPT *</Label>
            <Textarea id="a-raw" value={rawText} onChange={(e) => setRawText(e.target.value)} rows={10}
              placeholder={"1. What is React?\na) A database\nb) A JavaScript library\nc) A language\nd) An OS\nAnswer: b"}
              className="font-mono text-sm" data-testid="raw-text-input" />
            <Button onClick={handleParse} disabled={parsing} variant="outline" className="mt-3" data-testid="parse-btn">
              {parsing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Parse Questions
            </Button>
          </div>

          {parsedQuestions.length > 0 && (
            <div className="space-y-4" data-testid="parsed-preview">
              <p className="font-semibold text-slate-700">{parsedQuestions.length} question(s) parsed — confirm the correct answer (highlighted):</p>
              {parsedQuestions.map((q, qi) => (
                <div key={qi} className="border border-slate-200 rounded-lg p-4" data-testid={`parsed-q-${qi}`}>
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-medium text-slate-800">Q{qi + 1}. {q.question}</p>
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeQuestion(qi)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-1.5">
                    {q.options.map((opt, oi) => (
                      <button key={oi} onClick={() => setCorrect(qi, oi)}
                        data-testid={`parsed-q-${qi}-opt-${oi}`}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm border ${
                          q.correct_answer === oi ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-medium' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}>
                        <span className="font-semibold mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                        {q.correct_answer === oi && <span className="ml-2 text-xs">✓ Correct</span>}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <Button onClick={handleCreate} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-assessment-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                Create Assessment
              </Button>
            </div>
          )}
        </div>
        <ReportDialog reportDialog={reportDialog} setReportDialog={setReportDialog} />
      </div>
    );
  }

  // ============ SUBMISSIONS VIEW ============
  return (
    <div data-testid="assessment-submissions-view">
      <Button variant="ghost" onClick={() => { setView('list'); loadAssessments(); }} className="mb-4"><ArrowLeft className="h-4 w-4 mr-2" /> Back to Assessments</Button>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">{activeAssessment?.title}</h1>
          <p className="text-slate-500">Candidate submissions and AI reports</p>
        </div>
        <Button variant="outline" onClick={() => copyLink(activeAssessment.slug)} data-testid="copy-link-subs">
          <Copy className="h-4 w-4 mr-2" /> Copy Public Link
        </Button>
      </div>

      {subsLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="no-submissions">
          <p className="text-slate-500">No submissions yet. Share the public link with candidates.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Score</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((s) => (
                <TableRow key={s.id} data-testid={`submission-row-${s.id}`}>
                  <TableCell className="font-medium">{s.candidate_name}</TableCell>
                  <TableCell className="text-slate-500">{s.candidate_email}</TableCell>
                  <TableCell className="text-slate-500">{s.candidate_phone}</TableCell>
                  <TableCell>
                    <Badge className={s.percentage >= 70 ? 'bg-emerald-500' : s.percentage >= 50 ? 'bg-amber-500' : 'bg-red-500'}>
                      {s.score}/{s.total} ({s.percentage}%)
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-500">{s.submitted_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => toggleConnected(s)}
                        data-testid={`connect-toggle-${s.id}`}
                        className={s.connected
                          ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}
                      >
                        {s.connected ? <><CheckCircle2 className="h-4 w-4 mr-1" /> Connected</> : 'Connect Now'}
                      </Button>
                      <Select value={s.assigned_to || undefined} onValueChange={(v) => assignTo(s, v)}>
                        <SelectTrigger className="w-28 h-9" data-testid={`assign-select-${s.id}`}>
                          <SelectValue placeholder="Assign" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="KJ" data-testid={`assign-kj-${s.id}`}>KJ</SelectItem>
                          <SelectItem value="Team" data-testid={`assign-team-${s.id}`}>Team</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {s.report_generated ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => viewReport(s)} data-testid={`view-report-${s.id}`}><Eye className="h-4 w-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadPdf(s)} disabled={downloadingId === s.id} data-testid={`download-pdf-${s.id}`}>
                            {downloadingId === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => generateReport(s)} disabled={generatingId === s.id} data-testid={`generate-report-${s.id}`}>
                          {generatingId === s.id ? <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Generating</> : <><Sparkles className="h-4 w-4 mr-1" /> AI Report</>}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <ReportDialog reportDialog={reportDialog} setReportDialog={setReportDialog} />
    </div>
  );
}

const ReportDialog = ({ reportDialog, setReportDialog }) => {
  if (!reportDialog) return null;
  const r = reportDialog.report || {};
  return (
    <Dialog open={!!reportDialog} onOpenChange={(o) => !o && setReportDialog(null)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle>{reportDialog.candidate_name} — Assessment Report</DialogTitle>
          <DialogDescription>
            Score: {reportDialog.score}/{reportDialog.total} ({reportDialog.percentage}%) · Skill: {r.skill_level || 'N/A'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <h4 className="font-semibold text-slate-700 mb-1">Overall Assessment</h4>
            <p className="text-slate-600">{r.overall_assessment || 'N/A'}</p>
          </div>
          <div>
            <h4 className="font-semibold text-slate-700 mb-1">Strengths</h4>
            <ul className="list-disc list-inside text-slate-600 space-y-0.5">
              {(r.strengths || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-slate-700 mb-1">Areas for Improvement</h4>
            <ul className="list-disc list-inside text-slate-600 space-y-0.5">
              {(r.areas_for_improvement || []).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </div>
          <div>
            <h4 className="font-semibold text-slate-700 mb-1">Recommendation</h4>
            <Badge className={r.recommendation === 'hire' ? 'bg-emerald-500' : r.recommendation === 'consider' ? 'bg-amber-500' : 'bg-red-500'}>
              {(r.recommendation || 'N/A').toUpperCase().replace('_', ' ')}
            </Badge>
            <p className="text-slate-600 mt-1">{r.recommendation_text}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setReportDialog(null)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
